import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Stack,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Business,
  People,
} from '@mui/icons-material';
import { getToken } from '@/utils/storage';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

interface OEMProduct {
  id: number;
  code: string;
  name: string;
  description: string | null;
  category: string | null;
  per_user_fee: number;
  per_user_fee_unlimited: number | null;
  min_retail_price: number;
  recommended_price: number;
  features: string[];
  service_url: string | null;
  is_active: boolean;
  partner_count: number;
  subscriber_count: number;
  created_at: string;
}

interface ProductForm {
  code: string;
  name: string;
  description: string;
  category: string;
  per_user_fee: number;
  per_user_fee_unlimited: number;
  min_retail_price: number;
  recommended_price: number;
  features: string;
  service_url: string;
  is_active: boolean;
}

const initialForm: ProductForm = {
  code: '',
  name: '',
  description: '',
  category: '',
  per_user_fee: 500,
  per_user_fee_unlimited: 300,
  min_retail_price: 0,
  recommended_price: 9800,
  features: '',
  service_url: '',
  is_active: true,
};

export const OEMProductsPage = () => {
  const [products, setProducts] = useState<OEMProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ダイアログ状態
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<OEMProduct | null>(null);
  const [form, setForm] = useState<ProductForm>(initialForm);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/oem/platform/admin/products`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      setError('商品一覧の取得に失敗しました');
      console.error('Fetch products error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_BASE}/oem/platform/admin/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: form.code,
          name: form.name,
          description: form.description || null,
          category: form.category || null,
          per_user_fee: form.per_user_fee,
          min_retail_price: form.min_retail_price,
          recommended_price: form.recommended_price,
          features: form.features ? form.features.split('\n').filter(f => f.trim()) : [],
          service_url: form.service_url || null,
          is_active: form.is_active,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || '商品の作成に失敗しました');
      }
      setSuccess('商品を作成しました');
      setCreateDialogOpen(false);
      setForm(initialForm);
      fetchProducts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '商品の作成に失敗しました';
      setError(message);
    }
  };

  const handleUpdate = async () => {
    if (!selectedProduct) return;
    try {
      const res = await fetch(`${API_BASE}/oem/platform/admin/products/${selectedProduct.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          description: form.description || null,
          category: form.category || null,
          per_user_fee: form.per_user_fee,
          min_retail_price: form.min_retail_price,
          recommended_price: form.recommended_price,
          features: form.features ? form.features.split('\n').filter(f => f.trim()) : [],
          service_url: form.service_url || null,
          is_active: form.is_active,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || '商品の更新に失敗しました');
      }
      setSuccess('商品を更新しました');
      setEditDialogOpen(false);
      fetchProducts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '商品の更新に失敗しました';
      setError(message);
    }
  };

  const handleToggle = async (product: OEMProduct) => {
    try {
      const res = await fetch(`${API_BASE}/oem/platform/admin/products/${product.id}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || '状態の切り替えに失敗しました');
      }
      setSuccess(`${product.name} を${product.is_active ? '無効' : '有効'}にしました`);
      fetchProducts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '状態の切り替えに失敗しました';
      setError(message);
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;
    try {
      const res = await fetch(`${API_BASE}/oem/platform/admin/products/${selectedProduct.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || '商品の削除に失敗しました');
      }
      setSuccess('商品を削除しました');
      setDeleteDialogOpen(false);
      fetchProducts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '商品の削除に失敗しました';
      setError(message);
    }
  };

  const openEditDialog = (product: OEMProduct) => {
    setSelectedProduct(product);
    setForm({
      code: product.code,
      name: product.name,
      description: product.description || '',
      category: product.category || '',
      per_user_fee: product.per_user_fee,
      per_user_fee_unlimited: product.per_user_fee_unlimited || 0,
      min_retail_price: product.min_retail_price,
      recommended_price: product.recommended_price,
      features: product.features.join('\n'),
      service_url: product.service_url || '',
      is_active: product.is_active,
    });
    setEditDialogOpen(true);
  };

  if (loading && products.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            OEM商品管理
          </Typography>
          <Typography variant="body1" color="text.secondary">
            パートナーに提供する商品の登録・管理
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setForm(initialForm);
            setCreateDialogOpen(true);
          }}
        >
          商品追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* 統計 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4">{products.length}</Typography>
              <Typography variant="body2" color="text.secondary">全商品</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">
                {products.filter(p => p.is_active).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">提供中</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="info.main">
                {products.reduce((sum, p) => sum + p.partner_count, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">利用パートナー</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary.main">
                {products.reduce((sum, p) => sum + p.subscriber_count, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">総ユーザー</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>商品</TableCell>
                  <TableCell>ユーザー単価</TableCell>
                  <TableCell>推奨価格</TableCell>
                  <TableCell align="center">パートナー</TableCell>
                  <TableCell align="center">ユーザー</TableCell>
                  <TableCell align="center">状態</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">商品がありません</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.id} sx={{ opacity: product.is_active ? 1 : 0.5 }}>
                      <TableCell>
                        <Box>
                          <Typography fontWeight="medium">{product.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.code}
                          </Typography>
                          {product.category && (
                            <Chip label={product.category} size="small" sx={{ ml: 1 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography>¥{product.per_user_fee.toLocaleString()}/月</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Standard
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>¥{product.recommended_price.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Business fontSize="small" color="action" />
                          <Typography>{product.partner_count}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <People fontSize="small" color="action" />
                          <Typography>{product.subscriber_count}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={product.is_active ? '提供中' : '停止中'}
                          color={product.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="編集">
                            <IconButton size="small" onClick={() => openEditDialog(product)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={product.is_active ? '停止する' : '提供開始'}>
                            <IconButton
                              size="small"
                              color={product.is_active ? 'warning' : 'success'}
                              onClick={() => handleToggle(product)}
                            >
                              {product.is_active ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="削除">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedProduct(product);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={product.partner_count > 0 || product.subscriber_count > 0}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 作成ダイアログ */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>OEM商品を追加</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="商品コード"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
              helperText="半角英数字とハイフンのみ（例: lp-wizard）"
              fullWidth
              required
            />
            <TextField
              label="商品名"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="説明"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="カテゴリ"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              helperText="例: ai-tools, marketing"
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="ユーザー単価（Standard）"
                  type="number"
                  value={form.per_user_fee}
                  onChange={(e) => setForm({ ...form, per_user_fee: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円/月' }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="ユーザー単価（Unlimited）"
                  type="number"
                  value={form.per_user_fee_unlimited}
                  onChange={(e) => setForm({ ...form, per_user_fee_unlimited: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円/月' }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="最低販売価格"
                  type="number"
                  value={form.min_retail_price}
                  onChange={(e) => setForm({ ...form, min_retail_price: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円' }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="推奨販売価格"
                  type="number"
                  value={form.recommended_price}
                  onChange={(e) => setForm({ ...form, recommended_price: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円' }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label="機能一覧"
              value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })}
              multiline
              rows={3}
              helperText="1行に1つの機能を入力"
              fullWidth
            />
            <TextField
              label="サービスURL"
              value={form.service_url}
              onChange={(e) => setForm({ ...form, service_url: e.target.value })}
              placeholder="https://..."
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="すぐに提供開始する"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.code || !form.name || !form.per_user_fee}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>OEM商品を編集</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="商品コード"
              value={form.code}
              disabled
              fullWidth
            />
            <TextField
              label="商品名"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="説明"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="カテゴリ"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="ユーザー単価（Standard）"
                  type="number"
                  value={form.per_user_fee}
                  onChange={(e) => setForm({ ...form, per_user_fee: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円/月' }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="ユーザー単価（Unlimited）"
                  type="number"
                  value={form.per_user_fee_unlimited}
                  onChange={(e) => setForm({ ...form, per_user_fee_unlimited: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円/月' }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="最低販売価格"
                  type="number"
                  value={form.min_retail_price}
                  onChange={(e) => setForm({ ...form, min_retail_price: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円' }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="推奨販売価格"
                  type="number"
                  value={form.recommended_price}
                  onChange={(e) => setForm({ ...form, recommended_price: parseInt(e.target.value) || 0 })}
                  InputProps={{ endAdornment: '円' }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label="機能一覧"
              value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })}
              multiline
              rows={3}
              helperText="1行に1つの機能を入力"
              fullWidth
            />
            <TextField
              label="サービスURL"
              value={form.service_url}
              onChange={(e) => setForm({ ...form, service_url: e.target.value })}
              placeholder="https://..."
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="提供中"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleUpdate}>
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>商品を削除</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <>
              {(selectedProduct.partner_count > 0 || selectedProduct.subscriber_count > 0) ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  この商品は使用中のため削除できません。先に「停止中」に変更してください。
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  この操作は取り消せません。
                </Alert>
              )}
              <Typography>
                <strong>{selectedProduct.name}</strong>（{selectedProduct.code}）を削除しますか？
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={
              selectedProduct ? selectedProduct.partner_count > 0 || selectedProduct.subscriber_count > 0 : true
            }
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OEMProductsPage;
